import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

// Module-level cache so we don't refetch settings for every card render
let _lowStockThresholdCache = null;
let _lowStockPromise = null;
function useLowStockThreshold() {
  const [threshold, setThreshold] = useState(_lowStockThresholdCache ?? 3);
  useEffect(() => {
    if (_lowStockThresholdCache !== null) return;
    if (!_lowStockPromise) {
      _lowStockPromise = api.get("/settings")
        .then((r) => { _lowStockThresholdCache = parseInt(r.data.low_stock_threshold, 10) || 3; })
        .catch(() => { _lowStockThresholdCache = 3; });
    }
    _lowStockPromise.then(() => setThreshold(_lowStockThresholdCache));
  }, []);
  return threshold;
}

function stockBadge(stock, threshold) {
  if (typeof stock !== "number" || stock <= 0) return null;
  if (stock === 1) return { text: "LAST ONE", pulse: true };
  if (stock <= threshold) return { text: `Only ${stock} left`, pulse: false };
  return null;
}

export default function ProductCard({ product }) {
  const { add } = useCart();
  const threshold = useLowStockThreshold();
  const badge = stockBadge(product.stock, threshold);
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const pctOff = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group card-hover bg-[var(--cl-surface)] border border-white/5 rounded-md overflow-hidden flex flex-col"
    >
      <Link to={`/product/${product.id}`} className="block aspect-[3/4] overflow-hidden bg-black relative">
        <img
          src={product.images?.[0] || "https://images.unsplash.com/photo-1599873265732-4511510f0908"}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105"
          style={{ transition: "transform 0.5s ease" }}
        />
        {product.serial_number && (
          <span className="absolute top-3 left-3 bg-black/80 text-[var(--cl-gold)] text-xs px-2 py-1 rounded font-bold border border-[var(--cl-gold)]/30">
            {product.serial_number}
          </span>
        )}
        {onSale && (
          <span data-testid={`sale-badge-${product.id}`}
                className="absolute top-3 right-3 bg-[var(--cl-primary)] text-white text-[10px] px-2 py-1 rounded font-bold tracking-widest uppercase">
            −{pctOff}% OFF
          </span>
        )}
        {!onSale && product.attributes?.includes("autograph") && (
          <span className="absolute top-3 right-3 bg-[var(--cl-primary)] text-white text-[10px] px-2 py-1 rounded font-bold tracking-wider">
            AUTO
          </span>
        )}
        {badge && (
          <span data-testid={`stock-badge-${product.id}`}
                className={`absolute bottom-3 left-3 text-[10px] px-2 py-1 rounded font-bold tracking-widest uppercase text-white ${badge.pulse ? "bg-[var(--cl-primary)] animate-pulse" : "bg-black/80 border border-[var(--cl-orange)]/60 text-[var(--cl-orange)]"}`}>
            {badge.text}
          </span>
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-label text-zinc-500 mb-1">{product.sport}</div>
        <div className="font-medium text-sm leading-snug line-clamp-2 min-h-[2.5rem]">{product.title}</div>
        <div className="flex items-end justify-between mt-3 gap-3">
          <div className="flex flex-col leading-none">
            {onSale && (
              <span data-testid={`was-price-${product.id}`}
                    className="text-sm text-[var(--cl-primary)] line-through decoration-2">
                ${product.compare_at_price.toLocaleString()}
              </span>
            )}
            <span data-testid={`now-price-${product.id}`}
                  className={`font-display leading-none ${onSale ? "text-3xl mt-1" : "text-2xl"}`}>
              ${product.price.toLocaleString()}
            </span>
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => add(product)}
            className="text-xs font-bold tracking-widest uppercase border border-white/20 hover:border-[var(--cl-orange)] hover:text-[var(--cl-orange)] px-3 py-2 rounded transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
